import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { addDoc, collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { BillingPlanId } from '../config/billing';
import { getStripePriceIdForPlan } from '../config/billing';
import { firestore, functions } from './firebase';

type CheckoutSessionPayload = {
  userId: string;
  organizationId: string;
  planId: BillingPlanId;
  activeClinics: number;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string | null;
};

type CheckoutSessionResponse = {
  url?: string;
  sessionId?: string;
};

type BillingPortalPayload = {
  userId: string;
  returnUrl: string;
  customerEmail?: string | null;
};

type BillingPortalResponse = {
  url?: string;
};

type CancelSubscriptionPayload = {
  organizationId: string;
};

type CancelSubscriptionResponse = {
  status?: string;
};

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '';
const checkoutEndpoint = process.env.REACT_APP_STRIPE_CHECKOUT_ENDPOINT ?? '';
const billingPortalEndpoint = process.env.REACT_APP_STRIPE_BILLING_PORTAL_ENDPOINT ?? '';
const billingPortalLocale = process.env.REACT_APP_STRIPE_BILLING_PORTAL_LOCALE ?? 'auto';
const billingPortalConfigurationId = process.env.REACT_APP_STRIPE_BILLING_PORTAL_CONFIGURATION_ID ?? '';
const billingPortalCallableNames = [
  'ext-firestore-stripe-subscriptions-createPortalLink',
  'ext-firestore-stripe-payments-createPortalLink',
] as const;

let stripePromise: Promise<Stripe | null> | null = null;

function ensureStripePromise() {
  if (!publishableKey) {
    console.warn('Stripe publishable key is not configured.');
    return null;
  }
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

export async function getStripe(): Promise<Stripe | null> {
  const promise = ensureStripePromise();
  return promise ? promise : Promise.resolve(null);
}

async function ensureCustomerDocument(userId: string, customerEmail?: string | null) {
  const customerDoc = doc(firestore, 'customers', userId);
  await setDoc(
    customerDoc,
    {
      email: customerEmail ?? null,
    },
    { merge: true },
  );
}

async function createCheckoutSessionViaExtension(payload: CheckoutSessionPayload): Promise<CheckoutSessionResponse> {
  const priceId = getStripePriceIdForPlan(payload.planId);
  await ensureCustomerDocument(payload.userId, payload.customerEmail);

  const checkoutSessions = collection(firestore, 'customers', payload.userId, 'checkout_sessions');
  const sessionRef = await addDoc(checkoutSessions, {
    price: priceId,
    quantity: Math.max(1, payload.activeClinics || 1),
    mode: 'subscription',
    success_url: payload.successUrl,
    cancel_url: payload.cancelUrl,
    allow_promotion_codes: true,
    metadata: {
      organizationId: payload.organizationId,
      planId: payload.planId,
    },
  });

  return await new Promise<CheckoutSessionResponse>((resolve, reject) => {
    const unsubscribe = onSnapshot(
      sessionRef,
      snapshot => {
        const data = snapshot.data() as Record<string, unknown> | undefined;
        if (!data) {
          return;
        }
        if (data.error) {
          unsubscribe();
          const message =
            typeof data.error === 'object' && data.error !== null && 'message' in data.error
              ? String((data.error as { message?: unknown }).message)
              : String(data.error);
          reject(new Error(message || 'Stripe reported an error while creating checkout session.'));
          return;
        }
        if (data.url || data.sessionId) {
          unsubscribe();
          resolve({ url: data.url as string | undefined, sessionId: data.sessionId as string | undefined });
        }
      },
      error => {
        unsubscribe();
        reject(error);
      },
    );
  });
}

async function createBillingPortalViaExtension(payload: BillingPortalPayload): Promise<BillingPortalResponse> {
  await ensureCustomerDocument(payload.userId, payload.customerEmail);

  const callablePayload: { returnUrl: string; locale?: string; configuration?: string } = {
    returnUrl: payload.returnUrl,
  };
  if (billingPortalLocale) {
    callablePayload.locale = billingPortalLocale;
  }
  if (billingPortalConfigurationId) {
    callablePayload.configuration = billingPortalConfigurationId;
  }

  let lastError: unknown = null;
  for (const functionName of billingPortalCallableNames) {
    try {
      const createPortalLink = httpsCallable<
        { returnUrl: string; locale?: string; configuration?: string },
        { url?: string; error?: string }
      >(functions, functionName);
      const result = await createPortalLink(callablePayload);
      const data = result.data;
      if (data?.url) {
        return { url: data.url };
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      throw new Error('Stripe did not return a billing portal URL.');
    } catch (error) {
      lastError = error;
      const code =
        typeof error === 'object' && error !== null && 'code' in error
          ? String((error as { code?: unknown }).code ?? '')
          : '';
      if (code !== 'functions/not-found') {
        throw error;
      }
      continue;
    }
  }

  if (lastError) {
    throw lastError instanceof Error
      ? lastError
      : new Error('Stripe billing portal callable is not available.');
  }

  throw new Error('Stripe billing portal callable is not available.');
}

export async function createCheckoutSession(
  payload: CheckoutSessionPayload,
): Promise<CheckoutSessionResponse> {
  if (!payload.userId) {
    throw new Error('A user id is required to create a checkout session.');
  }

  if (checkoutEndpoint) {
    // Custom backend endpoint path retained for flexibility.
    const response = await fetch(checkoutEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Stripe request failed (${response.status}): ${text || response.statusText}`);
    }
    return (await response.json()) as CheckoutSessionResponse;
  }

  return createCheckoutSessionViaExtension(payload);
}

export async function redirectToCheckout(checkout: CheckoutSessionResponse): Promise<void> {
  if (checkout.url) {
    window.location.assign(checkout.url);
    return;
  }

  if (checkout.sessionId) {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe.js is not available.');
    }
    const { error } = await stripe.redirectToCheckout({ sessionId: checkout.sessionId });
    if (error) {
      throw error;
    }
    return;
  }

  throw new Error('Checkout session did not include a redirect URL or session id.');
}

export async function createBillingPortalSession(
  payload: BillingPortalPayload,
): Promise<BillingPortalResponse> {
  if (!payload.userId) {
    throw new Error('A user id is required to open the billing portal.');
  }

  if (billingPortalEndpoint) {
    const response = await fetch(billingPortalEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Stripe request failed (${response.status}): ${text || response.statusText}`);
    }
    return (await response.json()) as BillingPortalResponse;
  }

  return createBillingPortalViaExtension(payload);
}

export async function cancelStripeSubscription(
  payload: CancelSubscriptionPayload,
): Promise<CancelSubscriptionResponse> {
  if (!payload.organizationId) {
    throw new Error('An organization id is required to cancel the subscription.');
  }

  const callable = httpsCallable<CancelSubscriptionPayload, CancelSubscriptionResponse>(
    functions,
    'cancelStripeSubscription',
  );
  const result = await callable(payload);
  return result.data;
}
